// lib/list/listGenerationAgreement.ts
import type { ListGenerationAgreement, Message, ScriptDefinition, ScriptInstance } from '#types/types';

export function isListConfirmation(prompt: string): boolean {
  return /^(?:yes|yeah|yep|ok|okay|continue|go ahead|do it|oui|vas[- ]y|continuez|fais[- ]le)[.!\s]*$/i.test(prompt.trim());
}

export function resolveListGenerationAgreement(
  prompt: string,
  history: readonly Pick<Message, 'role' | 'content' | 'scriptsInstances'>[],
  scope: Pick<ListGenerationAgreement, 'projectId' | 'sessionId' | 'userId'>,
  isUserTurn = true,
): { brief: string; script?: ScriptInstance } | null {
  const conversation = history.filter(message => message.role !== 'system');
  const humanIndex = conversation.findLastIndex(message => message.role === 'user');
  const instruction = isUserTurn ? prompt : conversation[humanIndex]?.content ?? '';
  if (!isListConfirmation(instruction)) return instruction ? { brief: instruction } : null;
  // The request history may include the current user entry or end at the offer.
  const previous = isUserTurn && conversation.at(-1)?.role !== 'user'
    ? conversation.at(-1) : conversation[humanIndex - 1];
  if (previous?.role !== 'assistant' || previous.scriptsInstances?.length !== 1) return null;
  const script = previous.scriptsInstances[0]!;
  const agreement = script.listGenerationAgreement;
  if (!agreement || agreement.projectId !== scope.projectId || agreement.sessionId !== scope.sessionId
    || agreement.userId !== scope.userId || typeof agreement.brief !== 'string' || !agreement.brief.trim()) return null;
  if (isUserTurn) {
    if (agreement.consumed) return null;
    const failed = script.commands.length === 1 && script.commands[0]?.action_type === 'GENERATE_MEDIA'
      && script.commands[0]?.status === 'failed' && !/abort|cancel|stop/i.test(script.commands[0]?.error ?? '');
    if (!(agreement.proposal && script.commands.length === 0) && !failed) return null;
  }
  return { brief: agreement.brief, script };
}

export function listProposalBrief(script: ScriptDefinition): string | null {
  const brief = script.listGenerationProposal?.brief;
  if (typeof brief !== 'string' || !brief.trim() || brief.length > 2000
    || !Array.isArray(script.commands) || script.commands.length !== 0 || script.autoRecall === true
    || /\b(?:or|either|alternatively|manage|rename|delete|rebuild|edit|upscale)\b|\b(?:persona|reference|character|object)\s+(?:panel|sheet)\b|\b(?:build|generate|create|prepare|make)\s+(?:the\s+)?@[a-z0-9:-]+\s+(?:panel|sheet)\b|@(?:last\b|output:)|https?:\/\//i.test(brief)) return null;
  return brief.trim();
}
